// Castintech dashboard — Faturalar (Invoice Timeline) view modülü.
// Faz 22 PR-B — 6-state forward-only invoice kanban view.
// Backend: GET /castintech-api/invoice-history/all (Faz 22 PR-B — bu PR).
// SSE: invoice.created + invoice.status (Faz 18 PR-D) — view açıkken canlı invalidate.
//
// State erişimi: state.js direct import (state.faturaRows, state.faturaMeta).
// Utils: utils.js (escHtml/escAttr/$).
// Api: apiFetch (api.js merkezi error boundary).
//
// Kart şeması: invoice_number + client_slug + total_amount + currency + relative time.
// 6 kolon (forward-only state machine: draft/sent/pending/paid/overdue/cancelled).
// Faz 21 PR-B müşteriler view paterni mirror — drag-drop yok, inline render
// (Karpathy "Surgical Changes").

import { apiFetch } from '../api.js';
import { state } from '../state.js';
import { escHtml, escAttr, $ } from '../utils.js';
import { toast } from '../ui/toast.js';
import { renderError as dsRenderError, createSkeleton } from '../ui/state-display.js';
import type { InvoiceTimelineRow } from '../../types/api';

// Faz 48.D — Crystal "fatura doldur" tetikleyicisi. Placeholder draft için
// Paperclip issue açar.
let _fillBtnDelegated = false;
async function bindFaturaFillButtons(): Promise<void> {
  if (_fillBtnDelegated) return;
  const root = $('faturalar-content') || document.body;
  root.addEventListener('click', async (e: Event) => {
    const t = e.target as HTMLElement;
    const btn = t.closest('.fatura-fill-btn') as HTMLElement | null;
    if (!btn) return;
    e.stopPropagation();
    const id = btn.getAttribute('data-invoice-id');
    const num = btn.getAttribute('data-invoice-number') || '?';
    if (!id) {
      toast('Geçersiz fatura ID', 'error');
      return;
    }
    btn.setAttribute('disabled', 'true');
    btn.textContent = '⏳ Issue açılıyor…';
    try {
      const res = await apiFetch(`/castintech-api/invoices/${encodeURIComponent(id)}/request-crystal-fill`, { method: 'POST' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast(`Crystal'a iş açılamadı: ${err.error || res.status}`, 'error');
        btn.removeAttribute('disabled');
        btn.textContent = `📋 Crystal'a iş aç`;
        return;
      }
      const data = await res.json() as { paperclip_issue_id?: string };
      toast(`${num} → Crystal'a iş açıldı (issue=${(data.paperclip_issue_id || '').substring(0, 8)})`, 'success');
      btn.textContent = '✓ Açıldı';
    } catch (e) {
      toast(`Hata: ${(e as Error).message}`, 'error');
      btn.removeAttribute('disabled');
      btn.textContent = `📋 Crystal'a iş aç`;
    }
  });
  _fillBtnDelegated = true;
}

// F4-B (2026-05-26): client filter — kullanıcı seçtiği müşterinin faturalarını izole görür.
let _clientFilter = '';
let _statusFilter = '';

const STATES = ['draft', 'sent', 'pending', 'paid', 'overdue', 'cancelled'] as const;

const STATE_LABELS: Record<string, string> = {
  draft: 'Taslak',
  sent: 'Gönderildi',
  pending: 'Beklemede',
  paid: 'Ödendi',
  overdue: 'Gecikti',
  cancelled: 'İptal',
};

interface InvoiceTimelineResponse {
  meta: { count: number; generated_at: string };
  invoices: InvoiceTimelineRow[];
}

export async function loadFaturalar(): Promise<void> {
  // F4-A — İlk yüklemede skeleton (sayfa açıksa).
  if (state.currentPage === 'faturalar' && (!state.faturaRows || state.faturaRows.length === 0)) {
    const root = $('faturalar-content');
    if (root && !root.querySelector('.fatura-board')) {
      root.innerHTML = '';
      const sk = createSkeleton({ rows: 5, height: '64px', rowGap: '10px' });
      root.appendChild(sk);
    }
  }
  try {
    const r = await apiFetch('/castintech-api/invoice-history/all');
    if (!r.ok) throw new Error('invoice-history HTTP ' + r.status);
    const data = (await r.json()) as InvoiceTimelineResponse;
    state.faturaRows = Array.isArray(data.invoices) ? data.invoices : [];
    state.faturaMeta = data.meta || null;
    if (state.currentPage === 'faturalar') renderFaturalar();
  } catch (e) {
    console.warn('loadFaturalar failed:', (e as Error).message);
    // F4-A — Önceki davranış: sadece console.warn (sessiz fail). Yeni: UI'a yansıt.
    const root = $('faturalar-content');
    if (root && state.currentPage === 'faturalar') {
      dsRenderError(root, e as Error, {
        title: 'Faturalar yüklenemedi',
        description: 'Bağlanırken sorun çıktı: ' + (e as Error).message,
        onRetry: () => { void loadFaturalar(); },
      });
    }
  }
}

function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (!t) return '—';
  const diff = Date.now() - t;
  if (diff < 0) return 'şimdi';
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'bugün';
  if (days === 1) return '1 gün';
  if (days < 7) return `${days} gün`;
  if (days < 30) return `${Math.floor(days / 7)} hafta`;
  return `${Math.floor(days / 30)} ay`;
}

function formatAmount(amount: string | number, currency: string): string {
  const n = typeof amount === 'number' ? amount : parseFloat(String(amount));
  if (!isFinite(n)) return `— ${currency}`;
  // tr-TR locale (1.234,56 ₺ tarzı) — sade Number.toLocaleString.
  const formatted = n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const sym = currency === 'TRY' ? '₺' : currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency;
  return `${formatted} ${sym}`;
}

export function renderFaturalar(): void {
  const root = $('faturalar-content');
  if (!root) return;
  // Faz 48.D — Click delegation init (idempotent).
  bindFaturaFillButtons();

  const sub = $('faturalar-sub');
  if (sub) {
    const cnt = state.faturaRows ? state.faturaRows.length : 0;
    sub.textContent = state.faturaMeta
      ? `${cnt} fatura · 6-state forward-only timeline`
      : 'yükleniyor...';
  }

  if (!state.faturaRows || state.faturaRows.length === 0) {
    // Faz 47.2 S3.E — Empty state hint: kullanıcı "neden boş" anlasın.
    root.innerHTML = `<div class="empty-state">
      <div class="empty-icon">📭</div>
      <div class="empty-title">Fatura bulunamadı</div>
      <div class="empty-sub">Crystal agent fatura oluşturduğunda burada görünür. İlk fatura için Pearl → Crystal Paperclip handoff'ı bekleniyor.</div>
    </div>`;
    return;
  }

  // F4-B — Client + status filter dropdown'larını render et + apply
  const allClients = Array.from(new Set(state.faturaRows.map(i => i.client_slug))).sort();
  const filterBar = `
    <div class="fatura-filter-bar" style="display:flex;gap:12px;align-items:center;padding:12px;border-bottom:1px solid var(--border);background:var(--surface-elevated)">
      <label for="fatura-client-filter" style="font-size:12px;color:var(--muted)">Müşteri:</label>
      <select id="fatura-client-filter" class="dashboard-input" style="min-width:160px;padding:4px 8px">
        <option value="">Tümü (${allClients.length})</option>
        ${allClients.map(c => `<option value="${escAttr(c)}" ${_clientFilter === c ? 'selected' : ''}>${escHtml(c)}</option>`).join('')}
      </select>
      <label for="fatura-status-filter" style="font-size:12px;color:var(--muted);margin-left:12px">Durum:</label>
      <select id="fatura-status-filter" class="dashboard-input" style="min-width:140px;padding:4px 8px">
        <option value="">Tümü</option>
        ${STATES.map(s => `<option value="${s}" ${_statusFilter === s ? 'selected' : ''}>${escHtml(STATE_LABELS[s] || s)}</option>`).join('')}
      </select>
      <button class="btn btn-secondary" id="fatura-filter-clear" style="margin-left:auto;padding:4px 12px;font-size:12px">Temizle</button>
    </div>
  `;

  // Apply filters
  const filteredRows = state.faturaRows.filter(inv => {
    if (_clientFilter && inv.client_slug !== _clientFilter) return false;
    if (_statusFilter && inv.status !== _statusFilter) return false;
    return true;
  });

  // Group by status (filtered)
  const grouped: Record<string, InvoiceTimelineRow[]> = {};
  for (const s of STATES) grouped[s] = [];
  for (const inv of filteredRows) {
    if (grouped[inv.status]) grouped[inv.status].push(inv);
  }

  const html = STATES.map(s => {
    const rows = grouped[s];
    const cards = rows.map(inv => {
      // Faz 48.D — Placeholder draft için "Crystal'a doldur" butonu.
      const isPlaceholder = inv.status === 'draft' && inv.metadata
        && (inv.metadata as Record<string, unknown>).placeholder === true;
      const fillBtn = isPlaceholder
        ? `<button class="btn-icon fatura-fill-btn" data-invoice-id="${escAttr(String(inv.id))}" data-invoice-number="${escAttr(inv.invoice_number)}" style="font-size:10px;width:auto;padding:2px 8px;margin-top:4px;" title="Crystal agent'a doldurma issue'u aç">📋 Crystal'a iş aç</button>`
        : '';
      return `
      <div class="fatura-card" data-invoice="${escAttr(inv.invoice_number)}">
        <div class="fatura-number">${escHtml(inv.invoice_number)}${isPlaceholder ? ' <span style="font-size:9px;color:var(--muted)">(placeholder)</span>' : ''}</div>
        <div class="fatura-client">${escHtml(inv.client_slug)}</div>
        <div class="fatura-amount">${escHtml(formatAmount(inv.total_amount, inv.currency))}</div>
        <div class="fatura-meta">${escHtml(formatRelativeTime(inv.updated_at))}</div>
        ${fillBtn}
      </div>
    `;
    }).join('');
    return `
      <div class="fatura-column" data-state="${s}">
        <div class="fatura-column-header">
          <span>${escHtml(STATE_LABELS[s] || s)}</span>
          <span class="count">${rows.length}</span>
        </div>
        <div class="fatura-column-body">${cards || '<div class="fatura-col-empty">—</div>'}</div>
      </div>
    `;
  }).join('');

  root.innerHTML = filterBar + `<div class="fatura-board">${html}</div>`;

  // F4-B — Filter event handlers (her render'da yeniden bind — innerHTML replaceti)
  const clientSel = root.querySelector<HTMLSelectElement>('#fatura-client-filter');
  const statusSel = root.querySelector<HTMLSelectElement>('#fatura-status-filter');
  const clearBtn = root.querySelector<HTMLButtonElement>('#fatura-filter-clear');
  if (clientSel) clientSel.addEventListener('change', () => { _clientFilter = clientSel.value; renderFaturalar(); });
  if (statusSel) statusSel.addEventListener('change', () => { _statusFilter = statusSel.value; renderFaturalar(); });
  if (clearBtn) clearBtn.addEventListener('click', () => { _clientFilter = ''; _statusFilter = ''; renderFaturalar(); });
}
